import React from 'react';
import './PrivacyPolicy.css';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-container">
      <nav className="privacy-nav">
        <div className="logo">
          <img src="/funhives.png" alt="FunHives" />
        </div>
        <button onClick={() => navigate('/')} className="btn-back">
          Back to Home
        </button>
      </nav>

      <div className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: January 27, 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to FunHives ("we," "our," or "us"). We are committed to
            protecting your privacy and ensuring a safe gaming experience. This
            Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you use our quiz and geo-location guessing
            game platform.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <ul>
            <li>Email address</li>
            <li>Username or display name</li>
            <li>Profile information (optional)</li>
            <li>Country/region information</li>
          </ul>

          <h3>2.2 Game Data</h3>
          <ul>
            <li>Quiz answers and scores</li>
            <li>Geo-location guesses and accuracy</li>
            <li>Game progress and achievements</li>
            <li>Leaderboard rankings</li>
            <li>In-game interactions and chat messages</li>
          </ul>

          <h3>2.3 Technical Information</h3>
          <ul>
            <li>IP address</li>
            <li>Device type and operating system</li>
            <li>Browser type and version</li>
            <li>Game session data and timestamps</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h3>2.4 Location Data</h3>
          <p>
            For our geo-guessing games, we may collect approximate location data
            to enhance gameplay. This is used solely for game functionality and
            is not shared with third parties for marketing purposes.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>Provide and maintain game services</li>
            <li>Process game sessions and calculate scores</li>
            <li>Display leaderboards and player rankings</li>
            <li>Enable multiplayer features and player interactions</li>
            <li>Send game invitations and notifications</li>
            <li>Improve game features and user experience</li>
            <li>Prevent fraud and ensure platform security</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Information Sharing and Disclosure</h2>
          <h3>4.1 Public Information</h3>
          <p>
            Your username, scores, and leaderboard rankings may be visible to
            other players. You can control your privacy settings in your
            account.
          </p>

          <h3>4.2 Third-Party Services</h3>
          <p>We may share information with:</p>
          <ul>
            <li>Cloud hosting providers (AWS)</li>
            <li>Analytics services to improve our platform</li>
            <li>Authentication providers (Google OAuth)</li>
          </ul>

          <h3>4.3 Legal Requirements</h3>
          <p>
            We may disclose your information if required by law or to protect
            our rights, safety, or property.
          </p>
        </section>

        <section>
          <h2>5. Data Security</h2>
          <p>
            We implement industry-standard security measures to protect your
            information, including encryption, secure authentication, and
            regular security audits. However, no method of transmission over the
            internet is 100% secure.
          </p>
        </section>

        <section>
          <h2>6. Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to maintain your session,
            remember your preferences, and analyze platform usage. You can
            control cookie settings through your browser.
          </p>
        </section>

        <section>
          <h2>7. Children's Privacy</h2>
          <p>
            Our service is not intended for children under 13. We do not
            knowingly collect personal information from children under 13. If
            you believe we have collected information from a child under 13,
            please contact us immediately.
          </p>
        </section>

        <section>
          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal information</li>
            <li>Correct inaccurate data</li>
            <li>Request deletion of your account and data</li>
            <li>Opt-out of marketing communications</li>
            <li>Export your game data</li>
          </ul>
        </section>

        <section>
          <h2>9. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or
            as needed to provide services. You may request account deletion at
            any time, after which we will delete or anonymize your data within
            30 days.
          </p>
        </section>

        <section>
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            for such transfers.
          </p>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of significant changes via email or through the platform. Your
            continued use of FunHives after changes constitutes acceptance of
            the updated policy.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p>
            Email: <a href="mailto:play@funhives.com">play@funhives.com</a>
          </p>
        </section>
      </div>

      <footer className="privacy-footer">
        <p>&copy; 2026 FunHives. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
